import { memo, type JSX } from 'react';
import { styled } from '@mui/material';
import { corePalette } from '@/themes/colours';
import type { ITimelineMarker } from '@/types/PatientDashboard/PatientTimeline.types';
import MarkerDotCluster from './MarkerDotCluster';

const MarkerRoot = styled('span')({
  position: 'absolute',
  top: '50%',
  transform: 'translate(-4px, -50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  borderRadius: '6px',
  color: corePalette.offBlack100,
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '130%',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  '&[data-active="true"] .timeline-marker-dot, &[data-active="true"] .timeline-marker-drop': {
    boxShadow: `0 0 0 3px ${corePalette.green10}`,
  },
  '&[data-active="true"] .timeline-marker-dot-single': {
    transform: 'scale(1.45)',
  },
  '&[data-active="true"] .timeline-marker-dot-stack': {
    transform: 'scale(1.3)',
  },
  '&[data-active="true"] .timeline-marker-label': {
    color: corePalette.green150,
  },
});

const MarkerText = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  minWidth: 0,
});

const MarkerLabel = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const MarkerCount = styled('span')({
  flexShrink: 0,
});

interface IProps {
  marker: ITimelineMarker;
  left: number;
  isActive?: boolean;
  labelWidth?: number | null;
}

function Marker({
  marker,
  left,
  isActive,
  labelWidth,
}: IProps): JSX.Element {
  const extraCount = marker.groupCount && marker.groupCount > 1 ? marker.groupCount - 1 : 0;
  const showText = labelWidth !== null;

  return (
    <MarkerRoot
      data-active={isActive ? 'true' : undefined}
      style={{ left: `${left}%` }}
    >
      <MarkerDotCluster marker={marker} />
      {showText && (
        <MarkerText>
          <MarkerLabel
            className="timeline-marker-label"
            style={labelWidth != null ? { maxWidth: `${labelWidth}px` } : undefined}
          >
            {marker.label}
          </MarkerLabel>
          {extraCount > 0 && <MarkerCount>{`+${extraCount}`}</MarkerCount>}
        </MarkerText>
      )}
    </MarkerRoot>
  );
}

export default memo(Marker);
